import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { BrandResponse, ClientResponse, StoreResponse } from '@clarity/contract';
import type { RestaurantCategory } from '@clarity/contract';
import {
  useBrands,
  useCampaigns,
  useClients,
  useSetStoreCategory,
  useStores,
} from '../../queries/index.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { money, num } from '../format.js';
import { QueryState } from '../QueryState.js';
import { IconCampaigns } from '../../components/icons.js';

/** The category taxonomy, with human labels, for the admin category picker. */
const CATEGORY_OPTIONS: readonly { value: RestaurantCategory; label: string }[] = [
  { value: 'general', label: 'General / all' },
  { value: 'fast_food', label: 'Fast food' },
  { value: 'arabic', label: 'Arabic / Levantine' },
  { value: 'desserts', label: 'Desserts / sweets' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'drinks', label: 'Drinks / juices' },
  { value: 'cafe', label: 'Café / coffee' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'healthy', label: 'Healthy / salads' },
];

/**
 * Admin-only CAMPAIGNS OVERVIEW (clarity-campaign-matching) — the admin's
 * cross-client view of which campaigns each client's branches are enrolled in
 * (participated), with matched-order counts and campaign-driven sales, and a
 * link into each branch's full Campaigns surface.
 *
 * A client enrolls in monthly offers off-platform; we INFER participation from
 * the branch's orders (every discounted order follows a campaign). This surface
 * rolls that up for the admin: per client → per branch → the campaigns that
 * actually matched orders, so an account manager can see at a glance what each
 * restaurant is running and how much it drove.
 *
 * PURE READ (unified-data-gathering / ingestion-single-source): it reads the
 * already-persisted matches via the same `useCampaigns(storeId)` the client
 * surface uses — no pull, no re-derive. Admin-only: the route guard in
 * `AppRoutes` is the real gate, the hidden "Manage" nav entry the convenience,
 * and the server independently enforces tenant/role on the read.
 */
export function CampaignsOverview() {
  const clients = useClients();
  const brands = useBrands();
  const stores = useStores();

  const loading = clients.isLoading || brands.isLoading || stores.isLoading;
  const error = clients.isError || brands.isError || stores.isError;

  const clientRows: ClientResponse[] = clients.data?.status === 200 ? clients.data.body : [];
  const brandRows: BrandResponse[] = brands.data?.status === 200 ? brands.data.body : [];
  const storeRows: StoreResponse[] = stores.data?.status === 200 ? stores.data.body : [];

  // Group branches under their client (via brand → clientId).
  const byClient = useMemo(() => {
    const brandClient = new Map(brandRows.map((b) => [b.id, b.clientId]));
    const map = new Map<string, StoreResponse[]>();
    for (const s of storeRows) {
      const clientId = brandClient.get(s.brandId);
      if (!clientId) continue;
      const list = map.get(clientId) ?? [];
      list.push(s);
      map.set(clientId, list);
    }
    return map;
  }, [brandRows, storeRows]);

  return (
    <section className="campaigns-overview" aria-label="Campaigns overview">
      <div className="page-head">
        <div>
          <h2>Campaigns overview</h2>
          <p className="sub">
            Which campaigns each client&apos;s branches are enrolled in, inferred from their
            orders — matched orders and campaign-driven sales per branch.
          </p>
        </div>
      </div>

      <QueryState isLoading={loading} isError={error} label="Campaigns overview">
        {clientRows.length === 0 ? (
          <div className="card empty">
            <IconCampaigns className="empty__glyph" />
            <p>No clients yet.</p>
          </div>
        ) : (
          <div className="campaigns-overview__clients">
            {clientRows.map((client) => {
              const branches = byClient.get(client.id) ?? [];
              return (
                <div key={client.id} className="card campaigns-overview__client">
                  <div className="campaigns-overview__client-head">
                    <h3>{client.name}</h3>
                    <span className="muted num">
                      {num(branches.length)} branch{branches.length === 1 ? '' : 'es'}
                    </span>
                  </div>
                  {branches.length === 0 ? (
                    <p className="muted">No branches.</p>
                  ) : (
                    branches.map((store) => (
                      <StoreCampaignsRow key={store.id} client={client} store={store} />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}
      </QueryState>
    </section>
  );
}

/**
 * One branch's campaigns roll-up row. Calls `useCampaigns(store.id)` itself (so
 * the hook count is stable per rendered branch) and shows the headline matched
 * figures + the top participated campaigns, linking into the branch's full
 * Campaigns surface. A not-yet-onboarded branch is shown as such (the read is
 * gated), never as an error.
 */
function StoreCampaignsRow({ client, store }: { client: ClientResponse; store: StoreResponse }) {
  const notOnboarded = isNotYetOnboarded(store);
  const query = useCampaigns(store.id, !notOnboarded);
  const cur = store.profile?.currency ?? 'JOD';
  const setCategory = useSetStoreCategory();
  const currentCategory =
    (store.profile as { category?: RestaurantCategory } | undefined)?.category ?? 'general';

  function onCategoryChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const category = e.target.value as RestaurantCategory;
    setCategory.mutate({ params: { id: store.id }, body: { category } });
  }

  const data = query.data?.status === 200 ? query.data.body : null;
  const participated = data
    ? data.campaigns
        .filter((row) => (row.actuals?.matchedOrders ?? 0) > 0)
        .sort((a, b) => (b.actuals?.matchedOrders ?? 0) - (a.actuals?.matchedOrders ?? 0))
    : [];

  return (
    <div className="campaigns-overview__store">
      <div className="campaigns-overview__store-head">
        <div className="campaigns-overview__store-id">
          <Link
            to={`/c/${client.id}/store/${store.id}/campaigns`}
            className="campaigns-overview__store-link"
          >
            {store.name ?? store.externalStoreId}
          </Link>
          <span className="campaigns-overview__platform">{store.platformKey}</span>
          <label className="campaigns-overview__category">
            <span className="sr-only">Restaurant category for {store.name ?? store.externalStoreId}</span>
            <select
              value={currentCategory}
              onChange={onCategoryChange}
              disabled={setCategory.isPending}
              aria-label="Restaurant category"
            >
              {CATEGORY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {setCategory.isSuccess && (
            <span className="campaigns-overview__saved muted" role="status">
              saved — re-match to apply
            </span>
          )}
        </div>
        {notOnboarded ? (
          <span className="badge campaigns-overview__badge--pending">Not onboarded</span>
        ) : query.isLoading ? (
          <span className="muted">Loading…</span>
        ) : data ? (
          <span className="campaigns-overview__store-stat num">
            {num(data.rollup.ordersMatched)} / {num(data.rollup.ordersTotal)} matched ·{' '}
            {money(data.rollup.campaignDrivenSales, cur)} driven
          </span>
        ) : (
          <span className="muted">No campaign data</span>
        )}
      </div>

      {!notOnboarded && participated.length > 0 && (
        <ul className="campaigns-overview__list">
          {participated.slice(0, 6).map((row) => (
            <li key={row.campaign.id} className="campaigns-overview__chip">
              <span className="campaigns-overview__chip-name">{row.campaign.name}</span>
              <span className="campaigns-overview__chip-count num">
                {num(row.actuals?.matchedOrders ?? 0)}
              </span>
            </li>
          ))}
          {participated.length > 6 && (
            <li className="campaigns-overview__chip campaigns-overview__chip--more">
              +{num(participated.length - 6)} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
